import React, { useContext, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { TableContainer, Table, TableBody } from '@material-ui/core';
import classNames from 'classnames';
import _ from 'lodash';

import './CompetitorsTable.scss';
import { select, RootState } from '../../../configs/store';
import { RaceMarket, RaceCompetitor } from 'src/interfaces/RacingState';
import RacingDetailContext, { RacingDetailContextProps } from '../Context/RacingDetail.context';
import CompetitorsTableHead from './CompetitorsTableHead/CompetitorsTableHead';
import CompetitorsTableRow from './CompetitorsTableRow/CompetitorsTableRow';
import PickTable from './PickTable/PickTable';
import { checkIfCrossEvent } from 'src/utils/racingCombinations';

interface CompetitorsTableProps {
  selectedEvent: any,
  maxMarkets?: number,
  racingType?: string,
  payouts?: any,
  quickCharts?:any
}

export default function CompetitorsTable({ selectedEvent, maxMarkets = 1, racingType, payouts, quickCharts }: CompetitorsTableProps) {
  const { selectedMarket, official, setShowCompetitors, showCompetitors } = useContext(RacingDetailContext);
  const { crossEvents } = useSelector((state: RootState) => state.racing);
  const {
    meeting: {
      name,
      countryCode,
      country
    }
  } = useSelector((state: RootState) => state.racing);

  const baseMarket: RaceMarket = useSelector(select.racing.baseMarket);
  const { bets } = useSelector((state: RootState) => state.betslip);
  const dispatch = useDispatch();
  const [order, setOrder] = useState<RacingDetailContextProps['order']>('asc');
  const [orderBy, setOrderBy] = useState('startNo');

  const { event } = useParams<{ event: string }>();

  const marketsOfSameType = _.filter(selectedEvent.markets, { oddsType: selectedMarket.oddsType });
  const fixedOddsMarkets = _.filter(selectedEvent.markets, { oddsType: 3 });
  const selectedFixedOddsMarketIndex = selectedMarket.oddsType === 3 ? _.findIndex(fixedOddsMarkets, { code: selectedMarket.code }) : -1;


  useEffect(() => {
    if (crossEvents && setShowCompetitors) setShowCompetitors({ ...showCompetitors, total: crossEvents.length });
  }, [crossEvents]);

  /**
   * Returns competitors with added outcomes array.
   * Each element in array has a show boolean flag that dictates if it should be displayed or not.
   */
  const getOrganizedCompetitors = useCallback((competitors, crossEventMarkets = null): RaceCompetitor[] => {
    let _fixedOddsMarkets = fixedOddsMarkets;
    let foWinMarket = !official ? baseMarket : selectedEvent.markets[0];

    if (crossEventMarkets) {
      _fixedOddsMarkets = _.filter(crossEventMarkets, { oddsType: 3 });
      foWinMarket = _.find(_fixedOddsMarkets, m => m.typeCode === 20100);
    }

    if (quickCharts) {
      _fixedOddsMarkets = _.filter(selectedEvent.markets, { oddsType: 3 });
      foWinMarket = _.find(_fixedOddsMarkets, m => m.typeCode === 20100) || baseMarket;
    }

    return _.map(competitors, (item, index: number) => {
      item.outcomes = new Array();

      // Add Base Market to first array position since its odds are used with every market.
      if (foWinMarket) {
        let outcome = {};
        if (foWinMarket.outcomes && foWinMarket.outcomes.length > 1){
          outcome = foWinMarket.outcomes.length > index && foWinMarket.outcomes[index];
        }

        item.outcomes = [{
          show: true,
          marketCode: foWinMarket.code,
          marketName: foWinMarket.name,
          marketGenericName: foWinMarket.genericName,
          ...outcome
        }];
      }

      // Workaround until SP market is moved to fixed odds.
      if ((selectedMarket.typeCode === 15100 || selectedMarket.genericName === 'SP') && !official) {
        item.outcomes.push({
          show: true,
          marketCode: selectedMarket.code,
          marketName: selectedMarket.name,
          marketGenericName: selectedMarket.genericName,
          value: isNaN(item.outcomes.value) ? 'SP' : item.outcomes.value,
          name: item.horse?.name
        });
      }

      if (selectedMarket.oddsType === 3) {
        // We're only interested in odds for FO markets.
        for (let i = 0; i < _fixedOddsMarkets.length; i++) {
          const market = _fixedOddsMarkets[i];
          let outcome = market.outcomes && _.find(market.outcomes, o => o.competitorId === item.competitorId);

          if ((quickCharts && !outcome) && market.outcomes){
            outcome =  _.find(market.outcomes, o => o.name === item.horse.name);
          }
          // Check if market is not base market to avoid duplicates.
          if (!_.isEqual(market, foWinMarket)) {
            item.outcomes.push({
              show: i < maxMarkets || selectedMarket.code === market.code,
              marketCode: market.code,
              marketName: market.name,
              marketGenericName: market.genericName,
              name: item.name,
              ...outcome
            }
            );
          }
        }
      }

      return item;
    });
  }, [baseMarket, fixedOddsMarkets, selectedMarket, event]);

  const addingAllBets = (organizedCompetitors, market: RaceMarket) => {
    const newBets = [];
    const competitors = market.oddsType !== 2 ? market.outcomes : organizedCompetitors;

    const activeCompetitors = _.filter(competitors, (competitor) => {
      if (market.oddsType === 3){
        return competitor.statusNumber === 1 && competitor.value > 0;
      } else {
        return competitor.statusNumber === 1;
      }
    });
    for (let competitor of activeCompetitors) {

      if ('outcomes' in competitor) {
        competitor = { ...competitor, ...competitor.outcomes[0] };
      }

      const betToAdd = {
        eventCode: selectedEvent.eventCode,
        odds: market.oddsType === 3 ? competitor.value : null,
        marketOutcomeCode: market.oddsType === 3 ? competitor.code : market.outcomes[0].code,
        marketCode: market.code,
        marketName: market.name,
        tournamentName: name ? `${ name } ${ countryCode }` : `${ selectedEvent.name } ${ selectedEvent.countryCode }`,
        datetime: selectedEvent.eventStart * 1000,
        sport: '',
        outcome: competitor.name,
        marketStatusName: 'active',
        marketStatusNumber: 1,
        outcomeStatusName: 'active',
        outcomeStatusNumber: 1,
        eventName: name ? name : selectedEvent.name,
        type: 'racing',
        productType: 2,
        startNo: competitor.progNo || _.find(organizedCompetitors, { horse:{ name: competitor.name } })?.progNo,
        oddsType: 3,
        predictionCount: market.predictionCount,
        raceNo: selectedEvent.raceNo,
        horseType: 'FO',
        country: country ? country : selectedEvent.countryCode,
        racingType: racingType,
        prediction: {
          numbersToPredictCount: market.predictionCount,
          anyNumbers: [competitor.startNo ? competitor.startNo : competitor.competitorId],
          rankedNumbers:[],
          inOrder: false
        }
      };
      const isNewBet = !_.some(bets,
        (bet) => bet.outcome === betToAdd.outcome && bet.eventCode === betToAdd.eventCode && bet.marketName === betToAdd.marketName);

      if (isNewBet){
        newBets.push(betToAdd);
      }
    }
    dispatch.betslip.addBets(newBets);
  };

  const checkWeightColumn = (organizedCompetitors) => {
    let shouldExist = false;

    _.forEach(organizedCompetitors, (competitor) => {
      if (competitor?.weight !== 0 || competitor?.horse?.age !== 0) {
        shouldExist = true;
      }
    });

    return shouldExist;
  };

  const checkJockeyColumn = (organizedCompetitors) => {
    let shouldExist = false;

    _.forEach(organizedCompetitors, (competitor) => {
      if (competitor?.jockey?.name || competitor?.trainer?.name) {
        shouldExist = true;
      }
    });

    return shouldExist;
  };

  const competitorsTable = () => {
    const organizedCompetitors = getOrganizedCompetitors(selectedEvent.competitors);
    const spMarketIndex = _.findIndex(marketsOfSameType, { typeCode: 15100 });

    if (spMarketIndex > -1){
      const auxiliar = marketsOfSameType[1];
      marketsOfSameType[1] = marketsOfSameType[spMarketIndex];
      marketsOfSameType[spMarketIndex] = auxiliar;
    }

    const markets = _.slice(marketsOfSameType, 0, maxMarkets);

    if (selectedMarket.oddsType === 3 && maxMarkets === 1 && selectedFixedOddsMarketIndex !== 0) {
      markets.push(marketsOfSameType[selectedFixedOddsMarketIndex]);
    } else if (selectedMarket.oddsType === 2) {
      markets[0] = fixedOddsMarkets[0];
    }

    return (
      <Table size="small">
        <CompetitorsTableHead
          addingAllBets={ (market) => addingAllBets(organizedCompetitors, market) }
          competitorsSize={ _.filter(organizedCompetitors, { statusNumber: 1 }).length }
          markets={ markets }
          orderBy= { orderBy }
          quickcharts = { quickCharts }
          setOrderBy={ setOrderBy }
          order={ order }
          setOrder= { setOrder }
          shouldWeight={ checkWeightColumn(organizedCompetitors) }
          shouldJockey={ checkJockeyColumn(organizedCompetitors) }
          racingType={ racingType }
          payouts={ payouts }
        />
        <TableBody>
          {
            organizedCompetitors.length > 0 &&  _.map(_.orderBy(organizedCompetitors, orderBy, order), (competitor) => {
              competitor['status'] = competitor.outcomes[0].statusNumber;

              if (!competitor.statusNumber){
                competitor.statusNumber = competitor.outcomes[0].statusNumber;
                competitor.statusName = competitor.outcomes[0].statusName;
              }
              if (competitor.horse && competitor.statusNumber){
                return (
                  <CompetitorsTableRow
                    key={ competitor.competitorId }
                    event={ selectedEvent }
                    competitor={ competitor }
                    selectedMarketIndex={ selectedFixedOddsMarketIndex }
                    selectedMarketName={ selectedMarket.genericName }
                    ellipsed= { quickCharts && markets.length > 1 }
                    crossEvent={ crossEvents[0] }
                    meeting={ { name: name, country: country } }
                    racingType={ racingType }
                    shouldWeight={ checkWeightColumn(organizedCompetitors) }
                    shouldJockey={ checkJockeyColumn(organizedCompetitors) }
                    payouts={ payouts }
                  />
                );
              } else {
                return null;
              }
            })
          }
        </TableBody>
      </Table>
    );
  };

  const pickTable = () => {
    return _.map(crossEvents, (crossEvent, index) => {
      crossEvent.parentEvent.competitors = getOrganizedCompetitors(crossEvent.parentEvent.competitors, crossEvent.parentEvent.markets);
      return (
        <PickTable
          key={ `${ crossEvent.raceId } ${ crossEvent.parentEvent.startTime }` }
          crossEvent={ crossEvent.parentEvent }
          index={ +index }
        />
      );
    });
  };

  return (
    <TableContainer
      className={
        classNames('competitors-table', {
          'competitors-table--dark': checkIfCrossEvent(selectedMarket)
        })
      }
    >
      {
        !checkIfCrossEvent(selectedMarket)
          ? competitorsTable()
          : pickTable()
      }
    </TableContainer>
  );
}
